import type { Ref } from 'vue'
import { useAcademySearch } from '#academy/composables/useAcademySearch'
import { listedTokenNav, preTgeNav } from '#forgd/app/composables/nav'
import { BuildingLibraryIcon, ChartPieIcon, DocumentChartBarIcon } from '@heroicons/vue/24/outline'
import { hash } from 'ohash'

const navSubItems = [
  ...preTgeNav.map(item => ({ ...item, preTge: true })),
  ...listedTokenNav.map(item => ({ ...item, preTge: false })),
]
  .flatMap(item => (item.children || []).map(child => ({ icon: item.icon, iconComponent: item.iconComponent, preTge: item.preTge, ...child })))
  .filter(item => item.to) as { to: string, label: string, icon?: string, iconComponent?: Component, preTge: boolean, isProjectScoped?: boolean }[]

const filteredWorkbooks = navSubItems
  .filter(item => item.to.startsWith('https://docs.google.com'))
  .filter((item, index, self) => self.findIndex(t => t.to === item.to) === index)

const workbooks = filteredWorkbooks.map((item) => {
  const hasDuplicateLabel = filteredWorkbooks.filter(t => t.label === item.label).length > 1
  return {
    ...item,
    icon: DocumentChartBarIcon,
    ...(hasDuplicateLabel && { label: `${item.preTge ? 'Pre-TGE: ' : 'Post-TGE: '}${item.label}` }),
  }
})

export const workbooksSearchGroup = {
  key: 'workbooks',
  label: 'Forgd Workbooks',
  commands: workbooks.map(item => ({ ...item, id: hash(item) })),
}

export const publicResearchSearchGroup = {
  key: 'public-research',
  label: 'Forgd Research',
  commands: [
    {
      id: hash('tokenomics'),
      icon: ChartPieIcon,
      label: 'Tokenomics Research',
      to: '/research/tokenomics',
    },
    {
      id: hash('exchange-and-listing'),
      icon: BuildingLibraryIcon,
      label: 'Exchange & Listing Research',
      to: '/research/exchange-and-listing',
    },
  ],
}

/**
 * Use this composable to get the search groups for the app
 * @param searchTerm - The search term to use for the search
 * @returns searchGroups - The search groups for the app
 */
export function useAppSearch(searchTerm: Ref<string>) {
  const isLoggedIn = ref(false)
  const academySearchGroups = useAcademySearch(searchTerm)

  // Make it SSR friendly
  onMounted(() => {
    const auth = useAuth()
    isLoggedIn.value = auth.isLoggedIn
    watch(() => auth.isLoggedIn, (value) => {
      isLoggedIn.value = value
    })
  })

  const searchGroups = computed(() => {
    const inHouseTools = useInHouseToolsSearchGroup()
    if (isLoggedIn.value) {
      return [inHouseTools, publicResearchSearchGroup, workbooksSearchGroup, ...academySearchGroups.value]
    }
    return [publicResearchSearchGroup, ...academySearchGroups.value, inHouseTools, workbooksSearchGroup]
  })

  return {
    searchGroups,
  }
}
